import {
  useCreateWorkOrder,
  useDeleteWorkOrder,
  useGetWorkOrderSummary,
  useListVendorBills,
  useListWorkOrders,
  useUpdateWorkOrder,
  type WorkOrderIn,
  type WorkOrderOut,
} from '@myrentalmate/api-client';
import { Chip, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable } from '../../components/DataTable';
import {
  optional,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult, okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';
import { BILL_STATUS, CATEGORY, optionsOf, PRIORITY, STATUS } from './labels';
import {
  assigneeValue,
  parseAssignee,
  useAssigneePicker,
  useResidentPicker,
  useWherePicker,
  whereValue,
} from './usePickers';

const COLUMNS: Column<WorkOrderOut>[] = [
  {
    label: 'Request',
    render: (w) => (
      <>
        {w.title}
        <Typography variant="body2" color="text.secondary">
          {w.resident_name ?? CATEGORY[w.category]}
        </Typography>
      </>
    ),
  },
  { label: 'Where', render: (w) => w.place },
  {
    label: 'Priority',
    render: (w) => (
      <Chip size="small" color={PRIORITY[w.priority].color} label={PRIORITY[w.priority].label} />
    ),
  },
  {
    label: 'Status',
    render: (w) => (
      <Chip size="small" color={STATUS[w.status].color} label={STATUS[w.status].label} />
    ),
  },
  {
    label: 'Assigned to',
    render: (w) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <span>{w.assignee_name ?? '–'}</span>
        {w.vendor_insurance_expired && (
          <Tooltip title="This vendor's insurance has expired">
            <Chip size="small" color="error" label="Insurance" />
          </Tooltip>
        )}
      </Stack>
    ),
  },
  { label: 'Scheduled', render: (w) => (w.scheduled_on ? formatDate(w.scheduled_on) : '–') },
  { label: 'Billed', render: (w) => (Number(w.billed) ? formatMoney(w.billed) : '–') },
];

function BillsOnOrder({ workOrderId }: { workOrderId: number }) {
  const canRead = useCan('bills.read');
  const bills = okItems(
    useListVendorBills({ work_order_id: workOrderId }, { query: { enabled: canRead } }).data,
  );
  if (!canRead || bills.length === 0) return null;
  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      <Typography variant="h6">Bills</Typography>
      <DataTable
        rows={bills}
        rowKey={(b) => b.id}
        columns={[
          { label: 'Vendor', render: (b) => b.vendor_name },
          { label: 'Amount', align: 'right', render: (b) => formatMoney(b.amount) },
          {
            label: 'Status',
            render: (b) => (
              <Chip
                size="small"
                color={BILL_STATUS[b.status].color}
                label={BILL_STATUS[b.status].label}
              />
            ),
          },
        ]}
      />
    </Stack>
  );
}

export function WorkOrdersPage() {
  const canManage = useCan('maintenance.manage');
  const create = useCreateWorkOrder();
  const update = useUpdateWorkOrder();
  const remove = useDeleteWorkOrder();
  const summary = okData(useGetWorkOrderSummary().data);
  const where = useWherePicker();
  const assignees = useAssigneePicker();
  const residents = useResidentPicker();
  const [openOnly, setOpenOnly] = useState(true);

  const useRows = ({ q, page, pageSize }: ListParams) =>
    listResult(useListWorkOrders({ q, page, page_size: pageSize, open_only: openOnly }));

  function toBody(values: FormValues): WorkOrderIn {
    const resident = optional(values, 'resident_id');
    return {
      ...where.parse(required(values, 'where')),
      ...parseAssignee(String(values.assignee ?? '')),
      resident_id: resident ? Number(resident) : null,
      title: required(values, 'title'),
      description: optional(values, 'description'),
      category: required(values, 'category') as WorkOrderIn['category'],
      priority: required(values, 'priority') as WorkOrderIn['priority'],
      status: required(values, 'status') as WorkOrderIn['status'],
      scheduled_on: optional(values, 'scheduled_on'),
      permission_to_enter: Boolean(values.permission_to_enter),
      entry_notes: optional(values, 'entry_notes'),
      completion_notes: optional(values, 'completion_notes'),
    };
  }

  const fields: Field[] = [
    { name: 'title', label: 'What Needs Doing', required: true },
    { name: 'where', label: 'Where', type: 'select', required: true, options: where.options },
    {
      name: 'resident_id',
      label: 'Resident',
      type: 'select',
      options: residents,
      helperText: 'Only a work order naming a resident shows in their portal',
    },
    {
      name: 'category',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'general',
      options: optionsOf(CATEGORY),
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      required: true,
      defaultValue: 'normal',
      options: optionsOf(PRIORITY),
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'new',
      options: optionsOf(STATUS),
    },
    { name: 'assignee', label: 'Assigned To', type: 'select', options: assignees },
    { name: 'scheduled_on', label: 'Scheduled For', type: 'date' },
    { name: 'description', label: 'Details', type: 'multiline' },
    { name: 'permission_to_enter', label: 'OK to Enter if Nobody Is Home', type: 'checkbox' },
    {
      name: 'entry_notes',
      label: 'Entry Notes',
      helperText: 'Pets, best times. Sent to whoever does the work.',
    },
    {
      name: 'completion_notes',
      label: 'What Was Done',
      type: 'multiline',
      helperText: 'The resident can read this.',
    },
  ];

  const toValues = (w: WorkOrderOut): FormValues => ({
    title: w.title,
    where: whereValue(w),
    resident_id: w.resident_id === null ? '' : String(w.resident_id),
    category: w.category,
    priority: w.priority,
    status: w.status,
    assignee: assigneeValue(w),
    scheduled_on: w.scheduled_on ?? '',
    description: w.description ?? '',
    permission_to_enter: w.permission_to_enter,
    entry_notes: w.entry_notes ?? '',
    completion_notes: w.completion_notes ?? '',
  });

  return (
    <RecordPage
      title="Work Orders"
      singular="work order"
      description="Repairs from request to done. Emergencies come first."
      summary={
        <Stack spacing={1}>
          <StatRow>
            <StatTile label="Open" value={summary?.open} />
            <StatTile label="Nobody Assigned" value={summary?.unassigned} />
            <StatTile label="Emergencies" value={summary?.emergency} />
            <StatTile label="Past Scheduled Date" value={summary?.overdue} />
          </StatRow>
          <FormControlLabel
            label="Open Work Only"
            control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
          />
        </Stack>
      }
      fields={fields}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) =>
        update.mutateAsync({ workOrderId: recordId, data: toBody(values) })
      }
      remove={(recordId) => remove.mutateAsync({ workOrderId: recordId })}
      renderExtras={(row) => (
        <>
          <BillsOnOrder workOrderId={row.id} />
          <RecordExtras entityType="work_order" entityId={row.id} canManage={canManage} />
        </>
      )}
    />
  );
}
